import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appId", "portalId", "onComplete", "onCancel", "introScreen", "successScreen", "children"];
import { useState, useEffect } from 'react';
import { UnifiedAuthButton } from './UnifiedAuthButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { fetchAppParams, persistSecretId } from './DashworksApi';
import { captureException, toError } from './internal/ravenUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DashworksAuthButton - A higher-level component that wraps UnifiedAuthButton
 * and handles all Dashworks-specific API interactions.
 *
 * This component:
 * 1. Fetches app configuration from Dashworks RPC
 * 2. Handles authentication via UnifiedAuthButton
 * 3. Persists the secret ID back to Dashworks
 * 4. Manages loading and error states
 *
 * Usage:
 * ```tsx
 * <DashworksAuthButton
 *   appId={19764048}
 *   portalId={123}
 *   onComplete={(secretId) => console.log('Connected:', secretId)}
 * />
 * ```
 */
export const DashworksAuthButton = _ref => {
  let {
      appId,
      portalId,
      onComplete,
      onCancel,
      introScreen,
      successScreen,
      children
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [appConfig, setAppConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch app configuration on mount
  useEffect(() => {
    let isMounted = true;
    const loadAppConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const config = await fetchAppParams(appId);
        if (isMounted) {
          setAppConfig(config);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : `Failed to load configuration for app ID ${appId}`);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    void loadAppConfig();
    return () => {
      isMounted = false;
    };
  }, [appId]);
  const handleComplete = secretId => {
    if (!appConfig) return;

    // Handle persistence asynchronously without returning the promise
    void (async () => {
      try {
        await persistSecretId(appConfig.appId, secretId, portalId, appConfig.authenticatorId, appConfig.scope);
        onComplete === null || onComplete === void 0 || onComplete(secretId);
      } catch (persistError) {
        // Even if persistence fails, we still call onComplete since auth succeeded
        // The consuming app can decide how to handle persistence failures
        captureException(new Error('Failed to persist secret to Dashworks', {
          cause: toError(persistError)
        }), {
          extra: {
            appId
          }
        });
        onComplete === null || onComplete === void 0 || onComplete(secretId);
      }
    })();
  };

  // Loading state
  if (isLoading) {
    return /*#__PURE__*/_jsxs(View, {
      textAlign: "center",
      paddingBlock: "300",
      children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(Text, {
          children: "Loading app configuration..."
        })
      })]
    });
  }

  // Error state
  if (error) {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: "Configuration Error",
      children: /*#__PURE__*/_jsx(View, {
        paddingBlock: "200",
        paddingInline: "200",
        children: /*#__PURE__*/_jsx(Text, {
          children: error
        })
      })
    });
  }

  // Success state - render UnifiedAuthButton
  if (appConfig) {
    const buttonText = children || `Connect to ${appConfig.appName || `App ${appConfig.appId}`}`;
    return /*#__PURE__*/_jsx(UnifiedAuthButton, Object.assign({}, buttonProps, {
      portalId: portalId,
      authenticatorId: appConfig.authenticatorId,
      appName: appConfig.appName || `App ${appConfig.appId}`,
      oAuthScope: appConfig.scope,
      appId: appId,
      onComplete: handleComplete,
      onCancel: onCancel,
      introScreen: introScreen,
      successScreen: successScreen,
      children: buttonText
    }));
  }

  // Fallback - should not reach here
  return null;
};